import { motion, useScroll, useTransform } from "framer-motion";
import { VaultRing } from "./Logo";

const lineReveal = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 1, ease: [0.33, 1, 0.68, 1], delay: 0.25 + i * 0.16 },
  }),
};

const LINES = ["Nobody warned you", "it would be", "this lonely."];

export default function Hero() {
  const { scrollY } = useScroll();
  const ringY = useTransform(scrollY, [0, 800], [0, 220]);
  const fade = useTransform(scrollY, [0, 500], [1, 0]);

  const scrollToLoadCheck = () => {
    if (window.__lenis) {
      window.__lenis.scrollTo("#load-check", { offset: -40 });
    } else {
      document.getElementById("load-check")?.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section id="top" className="min-h-[100svh] flex flex-col items-center justify-center relative px-4 overflow-hidden pt-24 pb-16">
      <motion.div style={{ y: ringY, opacity: fade }} className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <VaultRing className="w-[140vw] sm:w-[90vw] max-w-[900px] text-gold opacity-[0.07] animate-spin-slow" />
      </motion.div>

      <div className="relative z-10 flex flex-col items-center text-center max-w-4xl mx-auto">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="font-mono text-xs tracking-[0.35em] text-gold mb-10"
          data-testid="hero-eyebrow"
        >
          FOR FOUNDERS RUNNING IT ALONE
        </motion.p>

        <h1 className="font-serif font-light tracking-tight leading-[1.02] text-[clamp(2.4rem,10.5vw,8rem)]" data-testid="hero-headline">
          {LINES.map((line, i) => (
            <span key={i} className="block overflow-hidden pb-1">
              <motion.span
                custom={i}
                variants={lineReveal}
                initial="hidden"
                animate="visible"
                className={`block whitespace-nowrap ${i === LINES.length - 1 ? "italic text-gold" : ""}`}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.95 }}
          className="mt-8 max-w-2xl text-base md:text-lg text-muted-foreground leading-relaxed"
          data-testid="hero-subhead"
        >
          The Keep is a small, invite-only room for founders who are the whole
          company. Real people, matched by hand, so nobody carries it alone.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.15 }}
          className="mt-12 flex flex-col items-center gap-5"
        >
          <button
            data-testid="tell-us-hardest-button"
            onClick={scrollToLoadCheck}
            className="cta-gold font-mono text-xs sm:text-sm tracking-[0.15em] sm:tracking-[0.2em] px-6 sm:px-10 py-4 whitespace-nowrap"
          >
            TELL US WHAT'S BEEN HARDEST
          </button>
          <p className="font-mono text-[11px] tracking-[0.2em] text-muted-foreground" data-testid="hero-small-line">
            FOUNDING SEATS ARE FREE. YOU HELP SHAPE WHAT THIS BECOMES.
          </p>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
        aria-hidden="true"
      >
        <div className="w-px h-14 bg-gradient-to-b from-gold/60 to-transparent" />
      </motion.div>
    </section>
  );
}
